import { Stack, Typography } from '@mui/material';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import { FC, useMemo } from 'react';
import type { GroupedMemberContentProps } from '../types';
import { isTieredMember } from '../utils/dynamic-line-item-formatters';
import { COLORS } from './styles';
import { UsageDataTable } from './UsageDataTable';
import { UsageItemChart } from './UsageItemChart';

export const GroupedLineItemContent: FC<GroupedMemberContentProps> = ({
  member,
  pricingModel,
  selectedPeriodIndex,
  measurementChartData,
  billingCycle,
  currency,
  customerId,
}) => {
  const periods = member.dynamicLineItemPeriods;
  const currentPeriod = periods[selectedPeriodIndex];
  const isRevShare = isTieredMember(member) && member.isRevShare;
  const meterInfo = member.meterInfo;

  const selectedPeriodRange = useMemo(() => {
    if (!currentPeriod) {
      return undefined;
    }

    return {
      from: vyDate(currentPeriod.fromDate).toDate(),
      to: vyDate(currentPeriod.toDate).toDate(),
    };
  }, [currentPeriod]);

  const effectiveChartPeriod = currentPeriod
    ? { startTime: vyDate(currentPeriod.fromDate).toDate(), endTime: vyDate(currentPeriod.toDate).toDate() }
    : billingCycle;

  const chartData = useMemo(() => {
    if (!meterInfo || !measurementChartData) {
      return null;
    }

    for (const productId of Object.keys(measurementChartData)) {
      const productMeterData = measurementChartData[productId];
      const meterData = productMeterData?.[meterInfo.meterId];
      if (meterData) {
        return {
          meterData: { [meterInfo.meterId]: meterData },
          meterName: meterInfo.name,
          meterUnits: meterInfo.unitsType,
          aggregationOperator: meterInfo.aggregationOperator,
        };
      }
    }
    return null;
  }, [meterInfo, measurementChartData]);

  const shouldShowChart =
    isNotNullOrUndefined(chartData) &&
    isNotNullOrUndefined(chartData.meterData) &&
    isNotNullOrUndefined(chartData.meterName) &&
    isNotNullOrUndefined(chartData.aggregationOperator);

  return (
    <Stack>
      <Typography variant="subtitle1" sx={{ px: 3, py: 1, fontWeight: 500 }}>
        {member.name ?? 'Unnamed variant'}
      </Typography>
      <Stack
        sx={{
          m: "6px 24px 24px 24px",
          border: `1px solid ${COLORS.border}`,
          borderRadius: "8px"
        }}>
        <UsageDataTable
          period={currentPeriod}
          pricingModel={pricingModel}
          currency={currency}
          isRevShare={isRevShare}
          aggregationOperator={meterInfo?.aggregationOperator}
        />
      </Stack>
      {shouldShowChart && chartData && (
        <UsageItemChart
          meterData={chartData.meterData}
          meterName={chartData.meterName}
          meterUnits={chartData.meterUnits}
          aggregationOperator={chartData.aggregationOperator}
          period={effectiveChartPeriod}
          currency={currency}
          customerId={customerId}
          selectedPeriodRange={selectedPeriodRange}
        />
      )}
    </Stack>
  );
};
